import React, { useEffect, useState, useMemo } from "react";
import { getAllPackages, updatePackageStatus } from "../../../services/Admin";
import formatDate from "../../../utils/formatDate";
import Loader from "../../components/loader/Loader";
import CustomTable from "../../components/custom-table/CustomTable";
import "./style.css";
import { FaCheck, FaTimes, FaClock } from "react-icons/fa";
import { toast } from 'react-toastify';

function AllPackages() {
  const [isLoading, setIsLoading] = useState(true);
  const [packageList, setPackageList] = useState([]);
  const [activeTab, setActiveTab] = useState("buy-package");
  const [updatingPackages, setUpdatingPackages] = useState(new Set());

  // Status buttons component with internal state management
  const StatusButtons = ({ packageId, initialStatus }) => {
    const [currentStatus, setCurrentStatus] = useState(initialStatus);
    const isUpdating = updatingPackages.has(packageId);
    
    const handleStatusClick = async (newStatus) => {
      if (newStatus === currentStatus) return;

      setUpdatingPackages(prev => new Set([...prev, packageId]));
      
      try {
        await updatePackageStatus(packageId, newStatus);
        toast.success(`Package status updated to ${newStatus}`);
        
        // Update local state
        setCurrentStatus(newStatus);
        
        // Update the main package list
        setPackageList(prevList => 
          prevList.map(pkg => 
            pkg.packageId === packageId 
              ? {
                  ...pkg,
                  status: (
                    <span
                      style={{
                        color:
                          newStatus === "Active"
                            ? "green"
                            : newStatus === "Inactive"
                            ? "red"
                            : "orange",
                      }}
                    >
                      {newStatus}
                    </span>
                  ),
                  originalStatus: newStatus
                }
              : pkg
          )
        );
      } catch (error) {
        toast.error("Failed to update package status");
        console.error(error);
      } finally {
        setUpdatingPackages(prev => {
          const newSet = new Set(prev);
          newSet.delete(packageId);
          return newSet;
        });
      }
    };
    
    return (
      <div className="status-buttons">
        <button
          className={`status-btn active ${currentStatus === 'Active' ? 'current' : ''}`}
          onClick={() => handleStatusClick('Active')}
          disabled={isUpdating || currentStatus === 'Active'}
          title="Set Active"
        >
          <FaCheck />
          <span>Active</span>
        </button>
        <button
          className={`status-btn inactive ${currentStatus === 'Inactive' ? 'current' : ''}`}
          onClick={() => handleStatusClick('Inactive')}
          disabled={isUpdating || currentStatus === 'Inactive'}
          title="Set Inactive"
        >
          <FaTimes />
          <span>Inactive</span>
        </button>
        <button
          className={`status-btn requested ${currentStatus === 'Requested' ? 'current' : ''}`}
          onClick={() => handleStatusClick('Requested')}
          disabled={isUpdating || currentStatus === 'Requested'}
          title="Set Requested"
        >
          <FaClock />
          <span>Requested</span>
        </button>
        {isUpdating && <span className="updating">Updating...</span>}
      </div>
    );
  };

  const columns = [
    { Header: "S.no", accessor: "sno" },
    { Header: "User ID", accessor: "userId" },
    { Header: "Name", accessor: "name" },
    { Header: "Status", accessor: "status" },
    { Header: "Package Type", accessor: "packageType" },
    { Header: "Amount", accessor: "amount" },
    { Header: "CGT Coin", accessor: "cgtCoin" },
    { Header: "Txn ID", accessor: "txnId" },
    { Header: "POI", accessor: "poi" },
    { Header: "Product Voucher", accessor: "productVoucher" },
    { Header: "Start Date", accessor: "startDate" },
    { Header: "Created At", accessor: "createdAt" },
    { Header: "Actions", accessor: "actions" },
  ];

  // Filter data based on active tab
  const filteredPackages = useMemo(() => {
    if (activeTab === "all") return packageList;
    
    return packageList.filter(pkg => {
      switch (activeTab) {
        case "buy-package":
          // Buy Package: has txnId (txHash)
          return pkg.originalTxnId && pkg.originalTxnId !== "-";
        case "retopup":
          // Retopup: doesn't have txnId (no txHash)
          return !pkg.originalTxnId || pkg.originalTxnId === "-";
        default:
          return true;
      }
    });
  }, [packageList, activeTab]);

  // Calculate tab counts
  const tabCounts = useMemo(() => {
    const buyPackage = packageList.filter(pkg => pkg.originalTxnId && pkg.originalTxnId !== "-").length;
    const retopup = packageList.filter(pkg => !pkg.originalTxnId || pkg.originalTxnId === "-").length;
    
    return {
      all: packageList.length,
      buyPackage,
      retopup
    };
  }, [packageList]);

  const fetchAllPackages = async () => {
    try {
      const packagesData = await getAllPackages();
      console.log("Packages Data:", packagesData);

      // Reverse the array to show newest packages first
      const reversedData = [...packagesData.data];

      const formattedData = reversedData.map((pkg, index) => ({
        sno: index + 1,
        packageId: pkg.packageId, // Store packageId for updates
        userId: pkg.userId || "-",
        name: pkg.name || "-",
        packageType: pkg.packageType || "-",
        amount: `$${pkg.packageAmount?.toFixed(2) || "0.00"}`,
        cgtCoin: pkg.cgtCoin || 0,
        txnId: pkg.txnId ? (
          <a
            href={`https://explorer.cryptogt.com/tx/${pkg.txnId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            BSC Scan
          </a>
        ) : "-",
        poi: pkg.poi || 0,
        productVoucher: pkg.productVoucher ? (
          <span style={{ color: "green" }}>Yes</span>
        ) : (
          <span style={{ color: "red" }}>No</span>
        ),
        startDate: pkg.startDate ? formatDate(pkg.startDate) : "-",
        status: (
          <span
            style={{
              color:
                pkg.status === "Active"
                  ? "green"
                  : pkg.status === "Inactive"
                  ? "red"
                  : "orange",
            }}
          >
            {pkg.status}
          </span>
        ),
        createdAt: pkg.createdAt ? formatDate(pkg.createdAt) : "N/A",
        actions: (
          <StatusButtons 
            packageId={pkg.packageId} 
            initialStatus={pkg.status}
          />
        ),
        // Store original data for filtering
        originalStatus: pkg.status,
        originalTxnId: pkg.txnId
      }));

      setPackageList(formattedData);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching packages:", error);
      toast.error("Failed to load packages");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllPackages();
  }, []);

  const tabs = [
    { id: "all", label: "All", count: tabCounts.all },
    { id: "buy-package", label: "Buy Package", count: tabCounts.buyPackage },
    { id: "retopup", label: "Retopup", count: tabCounts.retopup }
  ];

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <div className="investment-history">
          <h1 className="main-heading">All Packages</h1>
          <br />
          
          {/* Tab Navigation */}
          <div className="package-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
                <span className="tab-count">({tab.count})</span>
              </button>
            ))}
          </div>
          
          <br />
          <CustomTable
            data={filteredPackages}
            columns={columns}
            heading={`${tabs.find(t => t.id === activeTab)?.label || 'All'} (${filteredPackages.length})`}
          />
        </div>
      )}
    </>
  );
}

export default AllPackages;